import React, { useCallback, useEffect, useMemo, useState } from "react";
import LogoHeader from "./LogoHeader";
import { FiAlertCircle, FiPlus, FiRefreshCw } from "react-icons/fi";
import { Link, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "react-datepicker/dist/react-datepicker.css";
import { addDays, format } from "date-fns";
import { es } from "date-fns/locale";
import {
  diContenedorIncompleto,
  fetchAlbaranes,
  fetchCamiones,
  fetchObras,
  fetchTipos,
  fetchVertederos,
} from "../services/albaranes";
import {
  fetchCartasPorte,
  fetchMaquinas,
  fetchPlantasArido,
} from "../services/cartas_porte";
import {
  setAlbaranes,
  setCamiones,
  setObras,
  setTipos,
  setVertederos,
  setAlbaranPendiente,
  setCartasPorte,
  setCartaPortePendiente,
  setMaquinas,
  setPlantasArido,
  setUser,
} from "../redux/actions";
import { Detector } from "react-detect-offline";
import { enviarAlbaranPendiente } from "../pluggins/albaranes";
import { crearCartaPorte } from "../services/cartas_porte";
import https from "../pluggins/http";
import ModalSelectorTipo from "./ModalSelectorTipo";
import { useConnectionStatus } from "../hooks/useConnectionStatus";

function permitido(valor) {
  return valor === undefined || valor === null || valor === true || valor === 1 || valor === "1";
}

function permitidoContenedor(valor) {
  return valor === true || valor === 1 || valor === "1";
}

function opcionesCreacion(datos) {
  const opciones = [];
  if (permitido(datos?.puede_di)) opciones.push("di");
  if (permitidoContenedor(datos?.puede_di_contenedor)) opciones.push("di_contenedor");
  if (permitido(datos?.puede_carta_generica)) opciones.push("carta_maquina");
  if (permitido(datos?.puede_carta_arido)) opciones.push("carta_arido");
  return opciones;
}

function ListScreen() {
  const dispatch = useDispatch();
  const history = useHistory();
  const isOnline = useConnectionStatus(); // Hook para detectar conexión
  const [diaOffset, setDiaOffset] = useState(0); // 0 = hoy, 1 = mañana
  const startDate = useMemo(() => addDays(new Date(), diaOffset), [diaOffset]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [showModalSelector, setShowModalSelector] = useState(false);
  const [opcionesModal, setOpcionesModal] = useState(["di", "carta_maquina", "carta_arido"]);
  const [activeTab, setActiveTab] = useState("dis"); // "dis" o "cartas"

  // Función para formatear fecha de YYYY-MM-DD a DD/MM/YYYY
  const formatFecha = (fecha) => {
    if (!fecha) return "";
    const parts = fecha.split("-");
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return fecha;
  };
  
  const currentUser = useSelector((state) => state?.auth?.currentUser);
  const user = currentUser?.data;
  const rol = currentUser?.rol;

  const albaranes = useSelector((state) =>
    Array.isArray(state?.app?.albaranes) ? state?.app?.albaranes : []
  );
  const cartasPorte = useSelector((state) =>
    Array.isArray(state?.app?.cartasPorte) ? state?.app?.cartasPorte : []
  );
  const [isConnected, setIsConnected] = useState(false);

  const albaranPendiente = useSelector((state) => state?.app?.albaranPendiente);
  const cartaPortePendiente = useSelector((state) => state?.app?.cartaPortePendiente);

  var pathname = "albaranCreated";

  if (rol === "vertedero") pathname = "validation/";

  var isActive = false;

  const getAlbaranes = useCallback(
    async (clearAlbaranPendiente = false) => {
      setIsConnected(isOnline);
      if (isOnline) {
        const filtro = format(startDate, "dd/MM/yyyy");
        const response = await fetchAlbaranes({
          id: user.id,
          rol: rol,
          fecha: filtro,
        });
        dispatch(setAlbaranes(response, clearAlbaranPendiente));
      }
    },
    [dispatch, rol, startDate, user.id, isOnline]
  );

  const getCartasPorte = useCallback(async () => {
    if (isOnline) {
      try {
        const response = await fetchCartasPorte({
          id: user.id,
          rol: rol,
          fecha: format(startDate, "dd/MM/yyyy"),
        });
        dispatch(setCartasPorte(response));
      } catch (_error) {
        console.log("Error al obtener cartas de porte:", _error);
      }
    }
  }, [dispatch, rol, startDate, user.id, isOnline]);

  const getObras = useCallback(async () => {
    if (isOnline) {
      try {
        const response = await fetchObras({
          operador: user?.operador,
          conductor_id: user?.id,
        });
        dispatch(setObras(response));
      } catch (_error) {}
    }
  }, [dispatch, isOnline, user?.operador, user?.id]);

  const getTipos = useCallback(async () => {
    if (isOnline) {
      try {
        const response = await fetchTipos();
        dispatch(setTipos(response));
      } catch (_error) {}
    }
  }, [dispatch, isOnline]);

  const getCamiones = useCallback(async () => {
    if (isOnline) {
      try {
        const response = await fetchCamiones({
          id: user?.operador,
          conductor_id: user?.id,
        });
        dispatch(setCamiones(response));
      } catch (_error) {}
    }
  }, [dispatch, user?.operador, isOnline]);

  const getVertederos = useCallback(async () => {
    if (isOnline) {
      try {
        const response = await fetchVertederos(user?.operador);
        dispatch(setVertederos(response));
      } catch (_error) {}
    }
  }, [dispatch, isOnline, user?.operador]);

  const getMaquinas = useCallback(async () => {
    if (isOnline) {
      try {
        const response = await fetchMaquinas();
        dispatch(setMaquinas(response));
      } catch (_error) {
        console.log("Error al obtener máquinas:", _error);
      }
    }
  }, [dispatch, isOnline]);

  const getPlantasArido = useCallback(async () => {
    if (isOnline) {
      try {
        const response = await fetchPlantasArido();
        dispatch(setPlantasArido(response));
      } catch (_error) {
        console.log("Error al obtener plantas de árido:", _error);
      }
    }
  }, [dispatch, isOnline]);

  const processAlbaranPendiente = useCallback(async () => {
    if (isOnline && albaranPendiente) {
      try {
        const response = await enviarAlbaranPendiente(albaranPendiente);
        await getAlbaranes(true);
        if (response?.data?.data) {
          if (response?.data?.data && rol === "vertedero") {
            history.push("/Validation?id=" + response?.data?.data.id);
          }
          if (response?.data?.data && rol !== "vertedero") {
            if (albaranPendiente?.tipo === "contenedor") {
              // Sin pantalla azul: el listado ya se ha refresado.
              dispatch(setAlbaranPendiente(null));
            } else {
              history.push("/Confirmacion", {
                frase: "Albarán de vertido creado correctamente",
                tipoResiduo: albaranPendiente?.tipoResiduo,
                redirect: "/ListScreen",
              });
            }
          }
        }
      } catch (_error) {
        console.log("error", _error);
        alert("Error al crear el albarán");
        // mantener el albarán pendiente para reintentar más tarde
      }
    }
  }, [albaranPendiente, dispatch, getAlbaranes, history, rol]);

  const processCartaPortePendiente = useCallback(async () => {
    if (isOnline && cartaPortePendiente) {
      try {
        const response = await crearCartaPorte(cartaPortePendiente);
        await getCartasPorte();
        if (response?.id) {
          dispatch(setCartaPortePendiente(null));
          // Mostrar confirmación opcional
          console.log("Carta de porte creada correctamente:", response.id);
        }
      } catch (_error) {
        console.log("error al crear carta de porte", _error);
        // mantener la carta de porte pendiente para reintentar más tarde
      }
    }
  }, [cartaPortePendiente, dispatch, getCartasPorte]);

  const estadoImage = (estado) => {
    if (estado === "active") {
      isActive = true;
      // eslint-disable-next-line jsx-a11y/alt-text
      return <img src={require("../assets/images/play.png")} />;
    } else if (estado === "approved") {
      // eslint-disable-next-line jsx-a11y/alt-text
      return <img src={require("../assets/images/tick.png")} />;
    } else if (estado === "denied") {
      // eslint-disable-next-line jsx-a11y/alt-text
      return <img src={require("../assets/images/xv3.png")} />;
    } else if (estado === "scanned") {
      return <img src={require("../assets/images/wait.png")} />;
    } else if (estado === "pending") {
      return <img src={require("../assets/images/wait.png")} />;
    }
  };

  useEffect(() => {
    if (!user) return;
    getAlbaranes();
    getCartasPorte();
    if (!isFiltered) {
      setIsFiltered(true);
      getObras();
      getTipos();
      getCamiones();
      getVertederos();
      if (rol === "conductor") {
        getMaquinas();
        getPlantasArido();
      }
    }
  }, [
    getAlbaranes,
    getCartasPorte,
    getCamiones,
    getObras,
    getTipos,
    getVertederos,
    getMaquinas,
    getPlantasArido,
    isFiltered,
    user,
    rol,
    diaOffset,
  ]);

  useEffect(() => {
    if (albaranPendiente) {
      processAlbaranPendiente();
    }
  }, [albaranPendiente, processAlbaranPendiente]);

  useEffect(() => {
    if (cartaPortePendiente) {
      processCartaPortePendiente();
    }
  }, [cartaPortePendiente, processCartaPortePendiente]);
  
  sessionStorage.clear();

  const irACrear = (tipo) => {
    if (tipo === "di") {
      history.push({ pathname: "/DetallesObra", state: albaranes });
    } else if (tipo === "di_contenedor") {
      history.push({ pathname: "/DetallesContenedor" });
    } else if (tipo === "carta_maquina") {
      history.push({ pathname: "/CartaPorteForm", state: { tipo: "maquina" } });
    } else if (tipo === "carta_arido") {
      history.push({ pathname: "/CartaPorteForm", state: { tipo: "arido" } });
    }
  };

  const abrirCrear = async () => {
    let datos = user;
    if (isOnline && user?.id) {
      try {
        const response = await https.post("getConductor", { id: user.id });
        if (response?.data?.data) {
          datos = response.data.data;
          dispatch(setUser({ ...currentUser, data: datos }));
        }
      } catch (_error) {}
    }

    const opciones = opcionesCreacion(datos);
    if (opciones.length === 0) {
      window.alert("Tu empresa no te ha autorizado a crear documentos.");
      return;
    }
    if (opciones.length === 1) {
      irACrear(opciones[0]);
      return;
    }
    setOpcionesModal(opciones);
    setShowModalSelector(true);
  };
  //   const urlXlsx =
  //     "https://admin.icargoapp.com/public/file-export?id=" + user.id;
  //   const urlCsv =
  //     "https://admin.icargoapp.com/public/file-export-format?id=" + user.id;

  return (
    <div className="overflow-hidden">
      <div>
        <LogoHeader />
      </div>
      {/* {rol === "vertedero"?
            <div className="filterLista">
                <button className="filterBTn" onClick={handleClick}>
                    Filtrar por fecha
                </button>
                <button className="filterBTn-clear" onClick={handleClearClick}>
                    Limpiar
                </button>
                <div>
                    <a href={urlXlsx}  >
                        <button className="descargarBTnEx">Descargar Xlsx</button>
                    </a>
                    <a href={urlCsv}>
                        <button className="descargarBTnCsv">Descargar Csv</button>
                    </a>
                </div>
                {isOpen && (
                    <DatePicker 
                    locale={"es"}
                    selected={startDate} 
                    dateFormat="DD-MM-YYYY"
                    onChange={handleChange} 
                    inline />
                )}
            </div>
                :null} */}
      <div className="container">
        <button
          onClick={() => window.location.reload()}
          className="reload-button"
        >
          <FiRefreshCw size={24} />
        </button>
        <div className="list-container">
          <div className="detector--contaier">
            <Detector
              render={({ online }) =>
                !online && (
                  <div className="aviso--offline">
                    Estás sin conexión a internet
                  </div>
                )
              }
              onChange={(online) => {
                setIsConnected(online);
                if (!isConnected) {
                  getAlbaranes();
                }
                if (online) {
                  processAlbaranPendiente();
                  processCartaPortePendiente();
                }
              }}
            />
          </div>
          {rol === "conductor" && (
            <div className="dia-trabajo-bar">
              <span className="dia-trabajo-label">
                {diaOffset === 0
                  ? "Hoy"
                  : format(startDate, "EEEE d MMM", { locale: es })}
              </span>
              <button
                type="button"
                className="filterBTn"
                onClick={() => setDiaOffset((prev) => (prev === 0 ? 1 : 0))}
              >
                {diaOffset === 0 ? "Ver trabajo de mañana" : "Ver trabajo de hoy"}
              </button>
            </div>
          )}
          <div>
            {/* DOCUMENTOS DE IDENTIDAD (DIs) SECTION */}
            <div className="dis-section">
              <div className="section-header">
                <h3 className="section-title">📋 DIs</h3>
                {albaranes.length > 0 && (
                  <span className="document-count">{albaranes.length}</span>
                )}
              </div>
              
              {albaranPendiente && (
                <div className="item-container">
                  <div>
                    <p style={{ color: "gray" }}>
                      Creando albarán...
                      {albaranPendiente.ref} {formatFecha(albaranPendiente.fecha)}{" "}
                      {albaranPendiente.hora}
                    </p>
                  </div>
                  <div>{estadoImage("pending")}</div>
                </div>
              )}
              
              {albaranes.length > 0 ? (
                [...albaranes]
                  .sort((a, b) => (a.timeM > b.timeM ? 1 : -1))
                  .map((albaran) =>
                    rol === "conductor" ? (
                      <Link
                        key={albaran.id}
                        to={{
                          pathname: pathname,
                          albaran: albaran,
                        }}
                      >
                        <div className="item-container">
                          <div>
                            <p>
                              {albaran.ref} {formatFecha(albaran.fecha)} {albaran.hora}
                              {diContenedorIncompleto(albaran) && (
                                <FiAlertCircle
                                  title="Faltan datos"
                                  style={{ marginLeft: 6, color: "#d97706", verticalAlign: "middle" }}
                                />
                              )}
                            </p>{" "}
                          </div>
                          <div>{estadoImage(albaran.estado)}</div>
                        </div>
                      </Link>
                    ) : (
                      <Link
                        key={albaran.id}
                        to={{
                          pathname: pathname,
                          search: "?id=" + albaran.id + "&route=list",
                          albaran: albaran,
                        }}
                      >
                        <div className="item-container">
                          <div>
                            <p>
                              {albaran.ref} {formatFecha(albaran.fecha)} {albaran.hora}
                              {diContenedorIncompleto(albaran) && (
                                <FiAlertCircle
                                  title="Faltan datos"
                                  style={{ marginLeft: 6, color: "#d97706", verticalAlign: "middle" }}
                                />
                              )}
                            </p>{" "}
                          </div>
                          <div>{estadoImage(albaran.estado)}</div>
                        </div>
                      </Link>
                    )
                  )
              ) : (
                <div className="empty-state">
                  <p>No hay documentos de identidad pendientes</p>
                </div>
              )}
            </div>

            {/* CARTAS DE PORTE SECTION */}
            <div className="cartas-porte-section">
              <div className="section-header">
                <h3 className="section-title">🚚 Cartas de Porte</h3>
                {(cartasPorte.length + (cartaPortePendiente ? 1 : 0)) > 0 && (
                  <span className="document-count">{cartasPorte.length + (cartaPortePendiente ? 1 : 0)}</span>
                )}
              </div>
              
              {cartaPortePendiente && (
                <div className="item-container">
                  <div>
                    <p style={{ color: "gray" }}>
                      {cartaPortePendiente.ref} {formatFecha(cartaPortePendiente.fecha)}{" "}
                      {cartaPortePendiente.hora}
                    </p>
                  </div>
                  <div>{estadoImage("pending")}</div>
                </div>
              )}
              
              {(cartasPorte.length > 0 || cartaPortePendiente) ? (
                [...cartasPorte]
                  .sort((a, b) => (a.fecha > b.fecha ? -1 : 1))
                  .map((carta) => (
                    <Link
                      key={carta.id}
                      to={{
                        pathname: `/CartaPorteViewer/${carta.id}`,
                      }}
                    >
                      <div className="item-container">
                        <div>
                          <p>
                            {carta.ref} {formatFecha(carta.fecha)} {carta.hora}
                            {carta.tipo === "arido" && !carta.peso && (
                              <span title="Sin peso registrado" style={{ marginLeft: 6, fontSize: "0.85em", color: "#f59e0b" }}>⚖️</span>
                            )}
                          </p>
                        </div>
                        <div>{estadoImage(carta.estado)}</div>
                      </div>
                    </Link>
                  ))
              ) : (
                <div className="empty-state">
                  <p>No hay cartas de porte pendientes</p>
                </div>
              )}
            </div>
          </div>
          {rol === "conductor" ? (
            <div id="completPlus" className="toggleHide">
              <div className="circle">
                <button
                  className="divLinkDetalles"
                  onClick={abrirCrear}
                >
                  <div className="square">
                    <FiPlus id="plus" />
                  </div>
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {/* <input accept='image/*' id='icon-button-file' type='file' capture='environment'/> */}
      </div>

      <ModalSelectorTipo
        isOpen={showModalSelector}
        onClose={() => setShowModalSelector(false)}
        opciones={opcionesModal}
        onSelectTipo={irACrear}
      />
    </div>
  );
}

export default ListScreen;
